import React from 'react';
import { number, objectOf, oneOf, shape, string } from 'prop-types';
import classNames from 'classnames';

import { AspectRatioWrapper, ResponsiveImage } from '../../../../components/index.js';

import css from './Image.module.css';

// Images in markdown point to elsewhere (they don't support responsive image variants)
export const MarkdownImage = React.forwardRef((props, ref) => {
  const { className, rootClassName, loading = 'lazy', decoding = 'async', ...otherProps } = props;
  const classes = classNames(rootClassName || css.markdownImage, className);

  return (
    <img
      className={classes}
      loading={loading}
      decoding={decoding}
      {...otherProps}
      ref={ref}
      crossOrigin="anonymous"
    />
  );
});

MarkdownImage.displayName = 'MarkdownImage';

MarkdownImage.defaultProps = {
  rootClassName: null,
  className: null,
  alt: 'image',
};

MarkdownImage.propTypes = {
  rootClassName: string,
  className: string,
  src: string.isRequired,
  alt: string,
};

// Image as a Field (by default these are only allowed inside a block).
export const FieldImage = React.forwardRef((props, ref) => {
  // Content/section images rendered as Fields are always below the hero fold
  // (the hero background uses CustomAppearance, not FieldImage). Default them to
  // native lazy-loading + async decoding so they are not fetched during the
  // initial load — this keeps them out of Lighthouse's image-delivery accounting.
  // Both are overridable via props (e.g. an above-the-fold CMS image can pass
  // loading="eager"); native lazy-loading still fetches in/near-viewport images
  // promptly, so this does not delay anything actually visible on first paint.
  const {
    className,
    rootClassName,
    alt,
    image,
    sizes,
    loading = 'lazy',
    decoding = 'async',
    ...otherProps
  } = props;

  const { variants } = image?.attributes || {};
  const allVariantNames = Object.keys(variants);

  // The Sharetribe/imgix hosted-asset variants come signed (their `s=` param
  // means their width/format cannot be re-transformed in code), and typically
  // include 400w / 800w / 1200w. Field images are content-block media (cards,
  // illustrations, icons) that are never displayed anywhere near 1200px wide —
  // yet the generic column-based `sizes` makes the browser pull the 1200w
  // variant, which is exactly what Lighthouse's "Improve image delivery" flags
  // (e.g. 771×1200 downloaded for a ~520px slot). Cap the srcSet at 800w so the
  // oversized variant is never offered; 800w is ample for these blocks even at
  // 2x DPR. (The hero/LCP image uses CustomAppearance, not FieldImage, so it is
  // unaffected.) Fall back to all variants if none are <= the cap.
  // Cap on the LARGER dimension: a portrait 1200px-tall variant may report
  // width ~771 (fit=clip) but is still the heavy file, so a width-only cap would
  // let it through.
  //
  // Square (1:1) field images on this site are small feature ICONS displayed at
  // ~100px — the generic column `sizes` made them pull the 800w variant, which
  // is Lighthouse's remaining "Improve image delivery" savings. Photo cards are
  // never square (they are clipped landscape/portrait, e.g. 771×1200, 800×597).
  // So cap square images at 400w (the smallest signed variant imgix provides)
  // and everything else at 800w. Note: 400w is the code floor — a 100px icon
  // ideally wants a ~200px variant, which does NOT exist among the signed
  // variants, so fully eliminating the icon flag requires a smaller variant
  // configured in Sharetribe Console (or smaller icon source images).
  const edge = v => Math.max(v?.width || 0, v?.height || 0);
  const largestVariant = allVariantNames.reduce(
    (acc, name) => (edge(variants[name]) > edge(acc) ? variants[name] : acc),
    null
  );
  const isSquareIcon =
    largestVariant && Math.abs((largestVariant.width || 0) - (largestVariant.height || 0)) <= 2;
  const maxFieldImageEdge = isSquareIcon ? 400 : 800;
  const cappedVariantNames = allVariantNames.filter(
    name => edge(variants[name]) <= maxFieldImageEdge
  );
  const variantNames = cappedVariantNames.length ? cappedVariantNames : allVariantNames;

  // We assume aspect ratio from the first image variant
  const firstImageVariant = variants[variantNames[0]];
  const { width: aspectWidth, height: aspectHeight } = firstImageVariant || {};

  const classes = classNames(rootClassName || css.fieldImage, className);
  return (
    <AspectRatioWrapper className={classes} width={aspectWidth || 1} height={aspectHeight || 1}>
      <ResponsiveImage
        className={css.fieldImage}
        ref={ref}
        alt={alt}
        image={image}
        variants={variantNames}
        sizes={sizes}
        loading={loading}
        decoding={decoding}
        {...otherProps}
      />
    </AspectRatioWrapper>
  );
});

FieldImage.displayName = 'FieldImage';

FieldImage.defaultProps = {
  rootClassName: null,
  className: null,
  alt: 'image',
  sizes: null,
};

FieldImage.propTypes = {
  rootClassName: string,
  className: string,
  alt: string,
  image: shape({
    id: string.isRequired,
    type: oneOf(['imageAsset']).isRequired,
    attributes: shape({
      variants: objectOf(
        shape({
          width: number.isRequired,
          height: number.isRequired,
          url: string.isRequired,
        })
      ).isRequired,
    }).isRequired,
  }).isRequired,
  sizes: string,
};
